import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, Check, Plus, Target } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Eyebrow, PrimaryButton } from "@/components/fionova/Primitives";
import { getProgress, addGoal, addGoalUpdate, completeGoal } from "@/services/unsaidService";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

const GoalCard = ({ goal, onUpdate, onComplete, busy }) => {
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const isActive = goal.status === "active";
  return (
    <motion.div data-testid="progress-goal"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className={`rounded-3xl p-5 ring-1 ${isActive ? "bg-card ring-border" : "bg-primary/[0.06] ring-primary/30"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium leading-snug text-foreground">{goal.title}</p>
          {goal.note && <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{goal.note}</p>}
          <p className="eyebrow mt-2 text-[11px] text-muted-foreground">
            {isActive ? `Started ${fmt(goal.created_at)}` : `Completed ${fmt(goal.completed_at)}`}
          </p>
        </div>
        {isActive ? (
          <button data-testid="goal-complete" onClick={() => onComplete(goal.id)} disabled={busy}
            aria-label="Mark complete"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-border text-transparent transition-colors hover:border-primary/50">
            <Check className="h-4 w-4 text-foreground/40" strokeWidth={3} />
          </button>
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-4 w-4" strokeWidth={3} />
          </span>
        )}
      </div>

      {goal.updates?.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          {goal.updates.map((u, i) => (
            <div key={i} className="text-sm text-foreground/80">
              <span className="text-muted-foreground/70">{fmt(u.created_at)} · </span>{u.text}
            </div>
          ))}
        </div>
      )}

      {isActive && (
        <div className="mt-3">
          {open ? (
            <div className="flex items-end gap-2">
              <input data-testid="goal-update-input" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Add an update…"
                className="flex-1 rounded-2xl bg-background px-3 py-2 text-sm text-foreground ring-1 ring-border focus:outline-none focus:ring-2 focus:ring-primary" />
              <button data-testid="goal-update-save" disabled={!note.trim() || busy}
                onClick={async () => { await onUpdate(goal.id, note.trim()); setNote(""); setOpen(false); }}
                className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40">
                Add
              </button>
            </div>
          ) : (
            <button data-testid="goal-add-update" onClick={() => setOpen(true)}
              className="text-sm font-medium text-primary hover:underline">+ Add an update</button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default function ProgressPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [gnote, setGnote] = useState("");
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  const refresh = () => getProgress().then((d) => { setData(d); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { if (started.current) return; started.current = true; refresh(); }, []);

  const createGoal = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try { await addGoal(title.trim(), gnote.trim()); setTitle(""); setGnote(""); setShowForm(false); await refresh(); }
    catch {}
    setBusy(false);
  };
  const onUpdate = async (id, text) => { setBusy(true); try { await addGoalUpdate(id, text); await refresh(); } catch {} setBusy(false); };
  const onComplete = async (id) => { setBusy(true); try { await completeGoal(id); await refresh(); } catch {} setBusy(false); };

  const active = data?.goals?.active || [];
  const completed = data?.goals?.completed || [];
  const jcount = data?.journal?.count || 0;
  const checkins = data?.checkins || {};
  const totalCheckins = (checkins.xray?.count || 0) + (checkins.plan?.count || 0) + (checkins.purpose?.count || 0);
  const noHistory = loaded && active.length === 0 && completed.length === 0 && jcount === 0 && totalCheckins === 0;

  return (
    <div data-testid="page-progress" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto flex min-h-screen w-full max-w-[600px] flex-col px-6 py-8 sm:px-8">
        <div className="fio-appbar sticky top-3 z-20 flex items-center justify-between rounded-full py-1.5 pl-4 pr-1.5" data-testid="app-header">
          <button data-testid="progress-back" onClick={() => navigate("/app")}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </button>
          <Logo size={36} />
        </div>

        <div className="mt-8">
          <div className="flex items-center gap-2 text-primary">
            <TrendingUp className="h-4 w-4" />
            <Eyebrow>Progress</Eyebrow>
          </div>
          <h1 className="mt-2 text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
            Check your progress. Can you set a new goal?
          </h1>
        </div>

        {noHistory && (
          <div data-testid="progress-empty" className="mt-6 rounded-3xl bg-card p-6 ring-1 ring-border">
            <p className="text-base leading-relaxed text-muted-foreground">
              Your progress picture is just getting started. As you set goals, journal, and keep checking in with FioNova,
              real evidence of how far you've come will build here — no invented numbers, just your actual movement.
            </p>
          </div>
        )}

        {loaded && !noHistory && (
          <div className="mt-6 grid grid-cols-3 gap-3" data-testid="progress-stats">
            <div className="rounded-2xl bg-card p-4 text-center ring-1 ring-border">
              <p className="text-2xl font-semibold text-foreground">{completed.length}</p>
              <p className="mt-1 text-xs text-muted-foreground">Goals reached</p>
            </div>
            <div className="rounded-2xl bg-card p-4 text-center ring-1 ring-border">
              <p className="text-2xl font-semibold text-foreground">{jcount}</p>
              <p className="mt-1 text-xs text-muted-foreground">Journal entries</p>
            </div>
            <div className="rounded-2xl bg-card p-4 text-center ring-1 ring-border">
              <p className="text-2xl font-semibold text-foreground">{totalCheckins}</p>
              <p className="mt-1 text-xs text-muted-foreground">Check-ins</p>
            </div>
          </div>
        )}

        {/* Goals */}
        <div className="mt-10 flex items-center justify-between">
          <p className="eyebrow text-[11px] text-muted-foreground">Your goals</p>
          {!showForm && (
            <button data-testid="progress-new-goal" onClick={() => setShowForm(true)}
              className="flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              <Plus className="h-4 w-4" /> New goal
            </button>
          )}
        </div>

        {showForm && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-3 rounded-3xl bg-card p-5 ring-1 ring-border">
            <input data-testid="goal-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="What do you want to work toward?"
              className="w-full rounded-2xl bg-background px-4 py-3 text-base text-foreground ring-1 ring-border focus:outline-none focus:ring-2 focus:ring-primary" />
            <textarea data-testid="goal-note-input" rows={2} value={gnote} onChange={(e) => setGnote(e.target.value)}
              placeholder="Optional note — why this matters right now"
              className="mt-3 w-full resize-none rounded-2xl bg-background px-4 py-3 text-sm text-foreground ring-1 ring-border focus:outline-none focus:ring-2 focus:ring-primary" />
            <div className="mt-3 flex gap-2">
              <PrimaryButton data-testid="goal-create" onClick={createGoal} disabled={!title.trim() || busy}>Set goal</PrimaryButton>
              <button onClick={() => { setShowForm(false); setTitle(""); setGnote(""); }}
                className="rounded-full px-4 text-sm font-medium text-muted-foreground hover:text-foreground">Cancel</button>
            </div>
          </motion.div>
        )}

        {loaded && active.length === 0 && completed.length === 0 && !showForm && (
          <div className="mt-3 flex flex-col items-center rounded-3xl bg-card p-6 text-center ring-1 ring-border">
            <Target className="h-6 w-6 text-primary/50" strokeWidth={1.5} />
            <p className="mt-2 text-base leading-relaxed text-muted-foreground">
              You haven't set a goal yet. Naming one gives Ellie something concrete to walk toward with you.
            </p>
          </div>
        )}

        <div className="mt-3 space-y-3">
          {active.map((g) => <GoalCard key={g.id} goal={g} onUpdate={onUpdate} onComplete={onComplete} busy={busy} />)}
        </div>

        {completed.length > 0 && (
          <>
            <p className="eyebrow mt-8 text-[11px] text-muted-foreground">Reached</p>
            <div className="mt-3 space-y-3 pb-10">
              {completed.map((g) => <GoalCard key={g.id} goal={g} onUpdate={onUpdate} onComplete={onComplete} busy={busy} />)}
            </div>
          </>
        )}
        <div className="pb-10" />
      </div>
    </div>
  );
}
